import { useState } from 'react';
import { X, CreditCard, Sparkles, Mail, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useLibraryCard } from './LibraryCardContext';

const LibraryCardModal = ({ open, onClose, onSuccess }) => {
  const { register } = useLibraryCard();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const result = await register(email.trim(), name.trim());
      if (result.status === 'exists') {
        toast.success('Welcome back! Your library card has been loaded.');
      } else {
        toast.success('Library card created! You have 1 free token.');
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="library-card-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-slate-950 border border-slate-700/60 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="relative px-6 pt-8 pb-6 text-center bg-gradient-to-b from-cyan-950/40 to-transparent">
            <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white p-1 rounded-lg hover:bg-slate-800">
              <X className="w-5 h-5" />
            </button>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 mb-4">
              <CreditCard className="w-8 h-8 text-cyan-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-1" data-testid="library-card-title">
              Get Your Library Card
            </h2>
            <p className="text-sm text-slate-400 max-w-xs mx-auto">
              Register to access the full NextXus Library. Your first read is on us.
            </p>
          </div>

          {/* Free token callout */}
          <div className="mx-6 mb-5 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-300">1 Free Token Included</p>
              <p className="text-xs text-amber-400/70">Worth $5 — unlock any document instantly</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block font-medium">Email Address *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-slate-900/60 border-slate-700/60 focus:border-cyan-600"
                  required
                  data-testid="library-card-email"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block font-medium">Display Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-slate-900/60 border-slate-700/60 focus:border-cyan-600"
                  data-testid="library-card-name"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-medium h-11 text-sm"
              data-testid="library-card-submit"
            >
              {submitting ? 'Creating...' : 'Get My Library Card'}
            </Button>
            <p className="text-[11px] text-slate-600 text-center">
              No password needed. Your email is your library card.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LibraryCardModal;
